import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  Order,
  Promotion,
  OrderLineItemConnection,
  Maybe,
  FetchTransactionAccountV2Query,
  FetchTransactionAccountV2QueryVariables,
  InvoiceGroup,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { Field, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import TextArea from '@atlaskit/textarea';
import { map } from 'lodash';
import { InvoiceGroupFormField, SelectPromotionFormFieldV2, withApollo } from '@css-xp/base';
import { DFS_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { format } from 'date-fns';
import capitalize from 'lodash/capitalize';
import { fetchTransactionAccount } from '@css-xp/billing-v2';
import { SubscriptionTransferReasonCodes } from '@atlassian/cssxp-types-entitlement';
import { ReviewAndSubmitChange } from '../../transfer-entitlement/src/review-change';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import './entitlement-order-detail-pane.scss';
import Checkbox from '@atlaskit/checkbox';

interface Props {
  order: Order;
}

const NewBillDateContent = ({ orderLineItems }: { orderLineItems?: Maybe<OrderLineItemConnection> }) => {
  const nextBillingAnchorTimestamp = orderLineItems?.nodes[0]?.nextBillingAnchorTimestamp;
  if (nextBillingAnchorTimestamp) {
    const formatedDate = format(new Date(nextBillingAnchorTimestamp), DFS_DATE_FORMAT);
    return (
      <FormSection>
        <Field name="new-bill-date" defaultValue={formatedDate} label="New bill date">
          {({ fieldProps }) => <Textfield {...fieldProps} isDisabled name="new-bill-date" value={formatedDate} />}
        </Field>
      </FormSection>
    );
  }
  return null;
};

export const ORDERDETAILCOMPONENT = ({ order }: Props) => {
  const newTransactionAccountId = useMemo(() => {
    return order?.orderLineItems?.nodes[0]?.orderLineItemsAccountModification?.accountModificationTransactionAccountId;
  }, [order]);
  const { data: previousTransactionAccountData, loading } = useQuery<
    FetchTransactionAccountV2Query,
    FetchTransactionAccountV2QueryVariables
  >(fetchTransactionAccount, {
    variables: { id: order?.billTo?.billToAccountId ?? '' },
    fetchPolicy: 'no-cache',
    skip: !newTransactionAccountId,
  });

  const { data: newTransactionAccountData, loading: newLoading } = useQuery<
    FetchTransactionAccountV2Query,
    FetchTransactionAccountV2QueryVariables
  >(fetchTransactionAccount, {
    variables: {
      id: newTransactionAccountId ?? '',
    },
    fetchPolicy: 'no-cache',
    skip: !newTransactionAccountId,
  });

  const getNotes = () => {
    return map(order.orderLineItems?.nodes, 'orderLineItemsAdditionalNotes')
      ?.filter((note) => !!note)
      .map((note) => JSON.parse(note.replace(/'/g, '"')))
      .map((handNote) => {
        return handNote.note;
      })
      .join('\n');
  };

  const pricingPlanValue = useMemo(() => {
    const pricingPlan = order.orderLineItems?.nodes[0]?.pricingPlan;
    if (pricingPlan) {
      if (pricingPlan?.description) {
        const pricingPlanType = capitalize(pricingPlan?.pricingPlanType || '').replace(/_/g, ' ');
        return pricingPlan?.description + ' | ' + pricingPlanType + ' | ' + capitalize(pricingPlan?.status || '');
      }
      return pricingPlan?.type;
    }
    return 'N/A';
  }, [order]);

  const reasonCode = order?.metadataObj?.reasonCode;
  const currentBillingAdmin = order?.metadataObj?.currentBillingAdmin;
  const newBillingAdmin = order?.metadataObj?.newBillingAdmin;
  const previousTransactionAccount = previousTransactionAccountData?.transactionAccount;
  const newTransactionAccount = newTransactionAccountData?.transactionAccount;
  const previousInvoiceGroupId = useMemo(() => {
    return order?.billingGroup?.billingGroupId;
  }, [order]);
  const newInvoiceGroupId = useMemo(() => {
    return order?.orderLineItems?.nodes[0]?.orderLineItemsAccountModification?.accountModificationInvoiceGroupId;
  }, [order]);

  const previousInvoiceGroup = useMemo(() => {
    return previousTransactionAccount?.availableInvoiceGroups?.nodes?.find(
      (invoiceGroup) => previousInvoiceGroupId === invoiceGroup?.id
    );
  }, [previousTransactionAccount, previousInvoiceGroupId]);

  const newInvoiceGroup = useMemo(() => {
    return newTransactionAccount?.availableInvoiceGroups?.nodes?.find(
      (invoiceGroup) => newInvoiceGroupId === invoiceGroup?.id
    );
  }, [newTransactionAccount, newInvoiceGroupId]);

  return (
    <div className="entitlement-order-detail-pane" style={{ maxWidth: '600px' }}>
      <h3>Order details</h3>
      <FormSection>
        <Field name="orderId" defaultValue={order.orderNumber} label="Order number">
          {({ fieldProps }) => (
            <Textfield {...fieldProps} isDisabled name="orderId" value={order.orderNumber ?? 'N/A'} />
          )}
        </Field>
      </FormSection>
      <FormSection>
        <Field name="plan" defaultValue={order.orderLineItems?.nodes[0]?.offering?.name ?? 'N/A'} label="Plan">
          {({ fieldProps }) => (
            <Textfield
              {...fieldProps}
              isDisabled
              name="plan"
              value={order.orderLineItems?.nodes[0]?.offering?.name ?? 'N/A'}
            />
          )}
        </Field>
      </FormSection>
      <FormSection>
        <Field name="pricing-plan" defaultValue={pricingPlanValue} label="Pricing plan">
          {({ fieldProps }) => (
            <Textfield {...fieldProps} isDisabled name="pricing-plan" value={pricingPlanValue ?? 'N/A'} />
          )}
        </Field>
      </FormSection>
      <FormSection>
        {order.orderLineItems?.nodes[0]?.promotions && order.orderLineItems?.nodes[0]?.promotions[0] && (
          <SelectPromotionFormFieldV2
            promotions={order.orderLineItems?.nodes[0]?.promotions as Array<Promotion>}
            availablePromotions={order.orderLineItems?.nodes[0]?.promotions as Array<Promotion>}
            isDisabled={true}
            fieldLabel="Discounts"
            includeAllPromotions={true}
          />
        )}
      </FormSection>

      <FormSection>
        <Field
          name="billing-cadence"
          defaultValue={order.orderLineItems?.nodes[0]?.pricingPlan?.billingFrequency}
          label="Billing cycle"
        >
          {({ fieldProps }) => (
            <Textfield
              {...fieldProps}
              isDisabled
              name="billing-cadence"
              value={`${order.orderLineItems?.nodes[0]?.pricingPlan?.billingFrequency}`}
            />
          )}
        </Field>
      </FormSection>
      <NewBillDateContent orderLineItems={order.orderLineItems} />

      <FormSection>
        {order.invoiceGroup && (
          <InvoiceGroupFormField
            availableInvoiceGroups={[order.invoiceGroup]}
            entitlementBillingInvoiceGroupId={order.billingGroup?.billingGroupId}
            isDisabled={true}
            fieldLabel="Billing profile"
          />
        )}
      </FormSection>

      {reasonCode && (
        <FormSection>
          <Field
            name="reasonCode"
            defaultValue={SubscriptionTransferReasonCodes[reasonCode as keyof typeof SubscriptionTransferReasonCodes]}
            label="Reason code"
          >
            {({ fieldProps }) => (
              <Textfield
                {...fieldProps}
                isDisabled
                name="termsOfTransfer"
                value={SubscriptionTransferReasonCodes[reasonCode as keyof typeof SubscriptionTransferReasonCodes]}
                title={SubscriptionTransferReasonCodes[reasonCode as keyof typeof SubscriptionTransferReasonCodes]}
              />
            )}
          </Field>
        </FormSection>
      )}

      {order?.metadataObj?.termsOfTransfer && order.metadataObj.termsOfTransfer === 'true' && (
        <>
          <FormSection>
            <Field
              name="currentBillingAdmin"
              defaultValue={currentBillingAdmin}
              label="Previous billing account’s billing admin who accepted the terms of transfer"
            >
              {({ fieldProps }) => (
                <Textfield
                  {...fieldProps}
                  isDisabled
                  name="termsOfTransfer"
                  value={currentBillingAdmin as string}
                  title={currentBillingAdmin as string}
                />
              )}
            </Field>
          </FormSection>
          <FormSection>
            <Field
              name="newBillingAdmin"
              defaultValue={newBillingAdmin}
              label="New billing account’s billing admin who accepted the terms of transfer"
            >
              {({ fieldProps }) => (
                <Textfield
                  {...fieldProps}
                  isDisabled
                  name="termsOfTransfer"
                  value={newBillingAdmin as string}
                  title={newBillingAdmin as string}
                />
              )}
            </Field>
          </FormSection>
        </>
      )}

      <FormSection>
        <Field name="supportTicket" defaultValue={order.metadata?.supportTicketNumber} label="Support ticket number">
          {({ fieldProps }) => (
            <Textfield
              {...fieldProps}
              isDisabled
              name="supportTicket"
              value={`${order.metadata?.supportTicketNumber ?? 'N/A'}`}
            />
          )}
        </Field>
      </FormSection>

      {order?.metadataObj?.consentTaken && order.metadataObj.consentTaken === 'true' && (
        <Checkbox
          isChecked={true}
          isDisabled
          label="Customer consent has been received"
          name="consentTaken"
          testId="consent-taken"
        />
      )}

      <FormSection>
        <Field name="Comments" defaultValue={order.orderLineItems?.nodes[0]?.offering?.name} label="Internal comment">
          {() => <TextArea resize="auto" maxHeight="20vh" name="comments" isDisabled defaultValue={getNotes()} />}
        </Field>
      </FormSection>
      {newTransactionAccountId && (loading || newLoading) && (
        <div className="loading">
          <LoadingIndicator />
        </div>
      )}
      {newTransactionAccountId && previousTransactionAccount && newTransactionAccount && (
        <ReviewAndSubmitChange
          previousTransactionAccount={previousTransactionAccount as TransactionAccount}
          previousInvoiceGroup={previousInvoiceGroup as InvoiceGroup}
          previousBillingAdmin={currentBillingAdmin}
          newTransactionAccount={newTransactionAccount as TransactionAccount}
          newInvoiceGroup={newInvoiceGroup as InvoiceGroup}
          newBillingAdmin={newBillingAdmin}
        />
      )}
    </div>
  );
};

export const ORDERDETAIL = withApollo(ORDERDETAILCOMPONENT);
